import React, { useState, useEffect } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { addCampaign, updateCampaign } from '../../services/campaignService';
import { getAllTemplates } from '../../utils/emailTemplates';

const CampaignForm = ({ isOpen, onClose, onSave, campaign, title }) => {
  const initialFormState = {
    title: '',
    subject: '',
    body: ''
  };

  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  useEffect(() => {
    if (campaign) {
      setFormData({
        id: campaign.id,
        title: campaign.title || '',
        subject: campaign.subject || '',
        body: campaign.body || ''
      });
    } else {
      setFormData(initialFormState);
    }
  }, [campaign, isOpen]);

  useEffect(() => {
    setTemplates(getAllTemplates());
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.subject.trim()) newErrors.subject = 'Subject is required';
    if (!formData.body.trim()) newErrors.body = 'Email body is required';
    
    return newErrors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleEditorChange = (content) => {
    setFormData({ ...formData, body: content });
  };

  const handleTemplateChange = (e) => {
    const templateId = e.target.value;
    if (!templateId) return;
    
    const selectedTemplate = templates.find(template => template.id === templateId);
    if (selectedTemplate) {
      setFormData({
        ...formData,
        subject: selectedTemplate.subject,
        body: selectedTemplate.body
      });
    }
  };

  const togglePreview = () => {
    setShowPreview(!showPreview);
  };

  const EmailPreview = () => {
    return (
      <div className="mb-6">
        <div className="bg-gray-100 p-4 rounded-lg mb-4">
          <h2 className="font-bold text-xl mb-2">{formData.subject || 'Email Preview'}</h2>
          <div 
            className="email-preview bg-white p-5 rounded shadow border border-gray-200"
            dangerouslySetInnerHTML={{ __html: formData.body }}
          />
        </div>
      </div>
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const newErrors = validate();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      let savedCampaign;
      if (campaign?.id) {
        // Update existing campaign
        await updateCampaign(campaign.id, formData);
        savedCampaign = { ...formData, id: campaign.id };
      } else {
        // Add new campaign
        const docRef = await addCampaign(formData);
        savedCampaign = { ...formData, id: docRef.id, createdAt: new Date() };
      }
      
      onSave(savedCampaign);
      setFormData(initialFormState);
      setErrors({});
    } catch (error) {
      console.error('Error saving campaign:', error);
      setErrors({ submit: 'Failed to save campaign. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Configure Quill modules
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'script': 'sub'}, { 'script': 'super' }],
      [{ 'indent': '-1'}, { 'indent': '+1' }],
      [{ 'direction': 'rtl' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'font': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
  };

  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet', 'indent',
    'script',
    'direction',
    'color', 'background',
    'font', 'align',
    'link', 'image'
  ];

  return (
    isOpen ? (
      <div className="fixed z-10 inset-0 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen p-4">
          <div className="fixed inset-0 bg-black opacity-30" onClick={onClose}></div>
          
          <div className="relative bg-white rounded-lg w-full max-w-4xl mx-auto shadow-xl">
            <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
              <h2 className="text-lg font-medium text-gray-900">
                {title || 'Create Campaign'}
              </h2>
              <button
                onClick={togglePreview}
                type="button"
                className="inline-flex items-center px-3 py-1.5 border border-transparent text-xs font-medium rounded-md bg-indigo-100 text-indigo-700 hover:bg-indigo-200"
              >
                {showPreview ? 'Back to Edit' : 'Preview Email'}
              </button>
            </div>
            
            {showPreview ? (
              <div className="px-6 py-4">
                <EmailPreview />
                <div className="border-t border-gray-200 pt-4 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={isSubmitting}
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Campaign'}
                  </button>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="px-6 py-4">
                {errors.submit && (
                  <div className="mb-4 p-2 bg-red-100 border border-red-400 text-red-700 rounded">
                    {errors.submit}
                  </div>
                )}
                
                <div className="mb-4">
                  <label htmlFor="template" className="block text-sm font-medium text-gray-700 mb-1">
                    Choose a Template (Optional)
                  </label>
                  <select
                    id="template"
                    name="template"
                    onChange={handleTemplateChange}
                    className="shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md"
                  >
                    <option value="">-- Select a template --</option>
                    {templates.map(template => (
                      <option key={template.id} value={template.id}>{template.name}</option>
                    ))}
                  </select>
                </div>
                
                <div className="mb-4">
                  <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
                    Campaign Title*
                  </label>
                  <input
                    type="text"
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.title ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                </div>
                
                <div className="mb-4">
                  <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Subject*
                  </label>
                  <input
                    type="text"
                    id="subject"
                    name="subject"
                    value={formData.subject}
                    onChange={handleChange}
                    className={`shadow-sm focus:ring-indigo-500 focus:border-indigo-500 block w-full sm:text-sm border-gray-300 rounded-md ${
                      errors.subject ? 'border-red-500' : ''
                    }`}
                  />
                  {errors.subject && <p className="mt-1 text-sm text-red-600">{errors.subject}</p>}
                </div>
                
                <div className="mb-4">
                  <label htmlFor="body" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Body*
                  </label>
                  <div className={`${errors.body ? 'border border-red-500 rounded' : ''}`}>
                    <ReactQuill
                      theme="snow"
                      value={formData.body}
                      onChange={handleEditorChange}
                      modules={modules}
                      formats={formats}
                      style={{ height: '300px', marginBottom: '50px' }}
                      className="quill-editor"
                    />
                  </div>
                  {errors.body && <p className="mt-1 text-sm text-red-600">{errors.body}</p>}
                </div>
                
                <div className="pt-8 border-t border-gray-200 flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={onClose}
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="inline-flex justify-center px-4 py-2 text-sm font-medium text-white bg-indigo-600 border border-transparent rounded-md shadow-sm hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:bg-indigo-300"
                  >
                    {isSubmitting ? 'Saving...' : 'Save Campaign'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    ) : null
  );
};

export default CampaignForm;
